package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest

/**
 * Holds the set of **saved (bookmarked) Hidden Gems** as observable state, **persisted** via
 * [AppPreferences] so bookmarks survive restarts (the [FavoritesController] pattern). Guests stay
 * device-local; signed-in accounts additionally mirror to `users/{uid}/savedGems/{gemId}` through
 * [GemsRepository] — [run] performs the union-on-sign-in merge (local ∪ cloud, local-only ids
 * pushed up), mirroring `UserPreferencesSync`. Call [run] from an app-scoped coroutine when the
 * live backend is active; without it the controller is simply local-only.
 */
class GemsSavedController(
    private val preferences: AppPreferences,
    private val repository: GemsRepository,
) {
    private val _saved = MutableStateFlow(load())
    val saved: StateFlow<Set<SavedGem>> = _saved.asStateFlow()

    fun isSaved(gem: SavedGem): Boolean = gem in _saved.value

    /**
     * Adds the gem if absent, removes it if present; persists locally and emits the new set.
     * Returns the new saved state. Cloud mirroring is best-effort and must never block the UI —
     * a guest simply has no cloud write (Unauthorized is expected and ignored).
     */
    suspend fun toggle(gem: SavedGem): Boolean {
        if (gem.id.isBlank()) return false
        val nowSaved = gem !in _saved.value
        val next = _saved.value.toMutableSet().apply { if (nowSaved) add(gem) else remove(gem) }
        persist(next)
        repository.setSavedGemCloud(gem, nowSaved) // best-effort; result intentionally unused
        return nowSaved
    }

    /**
     * Runs until cancelled: whenever the account's cloud bookmarks stream emits, push local-only
     * gems up (they were saved while signed out) and adopt the union as the local set — saves
     * follow the user across devices without ever losing device-local ones.
     */
    suspend fun run() {
        repository.observeSavedGemsCloud().collectLatest { cloud ->
            if (cloud == null) return@collectLatest // signed out: stay local-only
            val local = _saved.value
            (local - cloud).forEach { repository.setSavedGemCloud(it, true) }
            persist(local + cloud)
        }
    }

    private fun load(): Set<SavedGem> =
        preferences.savedGemIds.mapNotNull(SavedGem::decode).toSet()

    private fun persist(gems: Set<SavedGem>) {
        preferences.savedGemIds = gems.map { it.encoded }.toSet()
        _saved.value = gems
    }
}
